/**
 * Lab Detail Activity matrix — periods × metrics from Strix analytics.
 *
 * Live: analytics-service `GET /v1/members/{id}/detail` (Agent scope) and
 * `GET /v1/agents/{id}/member-pnl` (All Members sum). Never the Lab
 * beta-bq-api.playexchwin.com MemberActivity endpoint.
 */
import { useQuery } from '@tanstack/react-query'
import { analyticsFetch, hasLiveAnalytics, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import type { AnalyticsMemberDetail } from '@/api/map/member'
import {
  ACTIVITY_PERIODS,
  activityPeriodRanges,
  emptyActivityMatrix,
  emptyPeriodFigures,
  figuresFromMemberPeriod,
  fillMetric,
  setPeriodFigures,
  type ActivityPeriod,
  type ActivityPeriodFigures,
  type MemberActivityMatrix,
} from '@/api/map/memberDetailLab'
import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned, type AnalyticsMemberPnlPage } from '@/api/map/pnl'
import { keys } from '@/api/hooks/shared/keys'
import * as api from '@/data/api'

export type MemberActivityMatrixQuery = {
  memberId: string
  memberScope: 'agent' | 'all-members'
  /** Custom Dates column — YYYY-MM-DD from the Activity filter pickers. */
  fromDate: string
  toDate: string
  gameId?: string
  subGame?: string
  typeName?: string
}

const PAGE_SIZE = 100
const MAX_PAGES = 50

function rangeQs(from: string, to: string): string {
  return new URLSearchParams({ from: from.slice(0, 10), to: to.slice(0, 10) }).toString()
}

async function loadAgentPeriod(
  memberId: string,
  from: string,
  to: string,
): Promise<ActivityPeriodFigures> {
  const live = await analyticsFetch<AnalyticsMemberDetail>(
    `/v1/members/${memberId}/detail?${rangeQs(from, to)}`,
  )
  return figuresFromMemberPeriod(live.period)
}

async function loadAllMembersPeriod(
  memberId: string,
  from: string,
  to: string,
): Promise<ActivityPeriodFigures> {
  const qs = rangeQs(from, to)
  let turnover = 0
  let pnl = 0
  for (let page = 0; page < MAX_PAGES; page++) {
    const live = await analyticsFetch<AnalyticsMemberPnlPage>(
      `/v1/agents/${memberId}/member-pnl?${qs}&page=${page}&size=${PAGE_SIZE}&sort=pnl`,
    )
    for (const row of live.rows ?? []) {
      turnover += fromMinorUnits(row.turnoverMinor)
      pnl +=
        row.memberNetMinor !== undefined
          ? fromMinorUnitsSigned(row.memberNetMinor)
          : -fromMinorUnitsSigned(row.bookNetMinor)
    }
    const got = live.rows?.length ?? 0
    const total = live.total
    if (got < PAGE_SIZE) break
    if (typeof total === 'number' && (page + 1) * PAGE_SIZE >= total) break
  }
  return {
    win: Math.max(0, pnl),
    betfairComm: 0,
    commission: 0,
    pnl,
    turnover,
  }
}

async function loadLiveMatrix(query: MemberActivityMatrixQuery): Promise<MemberActivityMatrix> {
  const base = emptyActivityMatrix(query.memberId, query.memberScope)
  const windows = activityPeriodRanges(query.fromDate, query.toDate)
  let next = base
  for (const { id } of ACTIVITY_PERIODS) {
    const { from, to } = windows[id]
    try {
      const figures =
        query.memberScope === 'all-members'
          ? await loadAllMembersPeriod(query.memberId, from, to)
          : await loadAgentPeriod(query.memberId, from, to)
      next = setPeriodFigures(next, id as ActivityPeriod, figures)
    } catch (err) {
      if (isUnreachableApiError(err)) {
        next = setPeriodFigures(next, id as ActivityPeriod, emptyPeriodFigures())
        continue
      }
      throw err
    }
  }
  return next
}

async function load(query: MemberActivityMatrixQuery): Promise<MemberActivityMatrix> {
  const base = emptyActivityMatrix(query.memberId, query.memberScope)
  if (hasLiveApi() && !hasLiveAnalytics()) {
    // Hierarchy up, analytics off — Lab empty sheet, same as other report pages.
    return base
  }
  if (hasLiveAnalytics()) {
    return loadLiveMatrix(query)
  }
  // Demo: roll lifetime-ish figures from fixture member P&L for this id.
  try {
    const rows = await api.getMemberPnl({
      rootId: query.memberId,
      range: {
        from: `${query.fromDate.slice(0, 10)}T00:00:00.000Z`,
        to: `${query.toDate.slice(0, 10)}T23:59:59.999Z`,
      },
      memberIds: query.memberScope === 'agent' ? [query.memberId] : undefined,
    })
    const self = rows.find((r) => r.account.id === query.memberId)
    const roll =
      query.memberScope === 'all-members'
        ? rows.reduce(
            (acc, r) => ({
              win: acc.win + Math.max(0, r.memberPnl),
              pnl: acc.pnl + r.memberPnl,
              turnover: acc.turnover + r.turnover,
              commission: acc.commission + r.commission,
            }),
            { win: 0, pnl: 0, turnover: 0, commission: 0 },
          )
        : {
            win: Math.max(0, self?.memberPnl ?? 0),
            pnl: self?.memberPnl ?? 0,
            turnover: self?.turnover ?? 0,
            commission: self?.commission ?? 0,
          }
    let next = fillMetric(base, 'win', roll.win)
    next = fillMetric(next, 'pnl', roll.pnl)
    next = fillMetric(next, 'turnover', roll.turnover)
    next = fillMetric(next, 'commission', roll.commission)
    next = fillMetric(next, 'betfairComm', 0)
    return next
  } catch {
    return base
  }
}

export function useMemberActivityMatrix(query: MemberActivityMatrixQuery, enabled = true) {
  return useQuery({
    queryKey: keys.memberActivityMatrix(query),
    queryFn: () => load(query),
    enabled: enabled && Boolean(query.memberId),
  })
}
